import { EntityWithUrl } from "@/utils/sos/utilities";
import { getSosEntityHashId, SOSEntityHashLink, SosEntityHashLinkProps } from "./SosEntityHashLink";

export interface SosEntityLineItemHashLinkProps extends SosEntityHashLinkProps {
  /**
   * The line number of the entity item to link to. Note that this doesn't need to be the true line number in the SOS system,
   * just a unique indexer for the line item within the entity. For example, if some of the line items are hidden or filtered out,
   * this can be a sequential index of the visible line items.
   */
  lineNumber: number;
}

/**
 * Renders a link that performs an internal navigation to the specified page, with the hash id set to that of a specific
 * entity line item, based on the provided entity type, id and line number.
 *
 * NOTE: The target of this link should either be currently rendered on the page, or be rendered with `<HashNavScrollTarget>`
 * to ensure the page scrolls to the correct position when the target component loads.
 */
export const SOSEntityLineItemHashLink: React.FC<SosEntityLineItemHashLinkProps> = ({ lineNumber, ...props }) => {
  return <SOSEntityHashLink {...props} idSuffix={getLineItemSuffix(lineNumber, props.idSuffix)} />;
};

/**
 * Gets the SOS entity hash id for a specific entity type, id and line number, optionally appending an additional suffix.
 */
export function getSosEntityLineItemHashId(
  type: EntityWithUrl,
  id: number,
  lineNumber: number,
  suffix?: string,
): string {
  return getSosEntityHashId(type, id, getLineItemSuffix(lineNumber, suffix));
}

function getLineItemSuffix(lineNumber: number, suffix?: string): string {
  return `line-${lineNumber.toFixed()}${suffix ? `-${suffix}` : ""}`;
}
